import { paymentApprovalError } from '../../../shared/models/payment-matching.model';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Invoice } from '../../../shared/models/invoice.model';
import {
  PaymentCategorization,
  PaymentCategorizationPage,
  PaymentMatch,
  PaymentMatchPage,
  PaymentMatchStatus,
  ZohoBankAccount,
} from '../../../shared/models/payment-matching.model';
import { InvoiceService } from '../../../shared/services/invoice.service';
import { ModalService } from '../../../shared/services/modal.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { PaymentMatchingService } from '../../../shared/services/payment-matching.service';

@Component({
  standalone: false,
  selector: 'app-payment-matching-index',
  templateUrl: './payment-matching-index.component.html',
  styleUrls: ['./payment-matching-index.component.scss'],
})
export class PaymentMatchingIndexComponent implements OnInit, OnDestroy {
  @ViewChild('detailsModalTpl', { read: TemplateRef, static: false }) detailsModalTpl: any;

  bankAccounts: ZohoBankAccount[] = [];
  invoice: Invoice | null = null;
  detailsLoading = false;
  activeTab: 'matches' | 'categorization' = 'matches';
  matches: PaymentMatch[] = [];
  totalElements = 0;
  totalPages = 0;
  currentPage = 0;
  pageSize = 20;
  selectedStatus: PaymentMatchStatus | null = null;
  readonly matchStatuses: { value: PaymentMatchStatus; label: string }[] = [
    { value: 'PENDING_REVIEW', label: 'Pending review' },
    { value: 'APPROVED', label: 'Approved' },
    { value: 'REJECTED', label: 'Rejected' },
    { value: 'APPLIED', label: 'Applied' },
    { value: 'FAILED', label: 'Failed' },
  ];
  categorizations: PaymentCategorization[] = [];
  categorizationTotalElements = 0;
  categorizationTotalPages = 0;
  categorizationCurrentPage = 0;

  autoMatchingEnabled = false;
  autoMatchingLoading = false;
  settingsLoaded = false;
  checkNowLoading = false;
  loadingMatches = false;
  loadingCategorizations = false;
  syncLoading = false;
  actionInProgress: { [matchId: string]: boolean } = {};
  categorizationActionInProgress: { [categorizationId: string]: boolean } = {};
  toggleInProgress: { [accountId: string]: boolean } = {};
  private statusPollInterval: any;

  constructor(
    private paymentMatchingService: PaymentMatchingService,
    private invoiceService: InvoiceService,
    private modal: ModalService,
    private notificationService: NotificationService,
    private titleService: Title
  ) {}

  ngOnInit(): void {
    this.titleService.setTitle('Payment Matching');
    this.loadSettings();
    this.loadBankAccounts();
    this.loadMatches();
    this.loadCategorizations();
    this.checkInitialStatus();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  loadBankAccounts(): void {
    this.paymentMatchingService.getBankAccounts().subscribe({
      next: accounts => (this.bankAccounts = accounts),
      error: () => this.notificationService.error('Failed to load bank accounts'),
    });
  }

  loadSettings(): void {
    this.paymentMatchingService.getSettings().subscribe({
      next: settings => {
        this.autoMatchingEnabled = settings.autoMatchingEnabled;
        this.settingsLoaded = true;
      },
      error: () => this.notificationService.error('Failed to load payment matching settings'),
    });
  }

  loadMatches(): void {
    this.loadingMatches = true;
    this.paymentMatchingService.getMatches(this.currentPage, this.pageSize, this.selectedStatus ?? undefined).subscribe({
      next: (page: PaymentMatchPage) => {
        this.matches = page.content;
        this.totalElements = page.totalElements;
        this.totalPages = page.totalPages;
        if (this.currentPage >= this.totalPages && this.totalPages > 0) {
          this.currentPage = this.totalPages - 1;
          this.loadMatches();
          return;
        }
        this.loadingMatches = false;
        this.actionInProgress = {};
      },
      error: () => {
        this.notificationService.error('Failed to load matches');
        this.loadingMatches = false;
        this.actionInProgress = {};
      },
    });
  }

  loadCategorizations(): void {
    this.loadingCategorizations = true;
    this.paymentMatchingService.getCategorizations(this.categorizationCurrentPage, this.pageSize).subscribe({
      next: (page: PaymentCategorizationPage) => {
        this.categorizations = page.content;
        this.categorizationTotalElements = page.totalElements;
        this.categorizationTotalPages = page.totalPages;
        if (this.categorizationCurrentPage >= this.categorizationTotalPages && this.categorizationTotalPages > 0) {
          this.categorizationCurrentPage = this.categorizationTotalPages - 1;
          this.loadCategorizations();
          return;
        }
        this.loadingCategorizations = false;
        this.categorizationActionInProgress = {};
      },
      error: () => {
        this.notificationService.error('Failed to load categorizations');
        this.loadingCategorizations = false;
        this.categorizationActionInProgress = {};
      },
    });
  }

  syncBankAccounts(): void {
    this.syncLoading = true;
    this.paymentMatchingService.syncBankAccounts().subscribe({
      next: accounts => {
        this.bankAccounts = accounts;
        this.syncLoading = false;
        this.notificationService.success('Bank accounts synced from Zoho');
      },
      error: () => {
        this.notificationService.error('Failed to sync bank accounts');
        this.syncLoading = false;
      },
    });
  }

  toggleBankAccount(account: ZohoBankAccount): void {
    const newEnabled = !account.enabled;
    this.toggleInProgress[account.id] = true;
    this.paymentMatchingService.toggleBankAccount(account.id, newEnabled).subscribe({
      next: () => {
        account.enabled = newEnabled;
        this.toggleInProgress[account.id] = false;
      },
      error: () => {
        this.notificationService.error('Failed to update bank account');
        this.toggleInProgress[account.id] = false;
      },
    });
  }

  toggleNearAmountMatching(account: ZohoBankAccount): void {
    const newEnabled = !account.allowNearAmountMatching;
    this.toggleInProgress[account.id] = true;
    this.paymentMatchingService.toggleBankAccountNearAmountMatching(account.id, newEnabled).subscribe({
      next: () => {
        account.allowNearAmountMatching = newEnabled;
        this.toggleInProgress[account.id] = false;
      },
      error: () => {
        this.notificationService.error('Failed to update near-amount matching');
        this.toggleInProgress[account.id] = false;
      },
    });
  }

  toggleCategorization(account: ZohoBankAccount): void {
    const newEnabled = !account.allowCategorization;
    this.toggleInProgress[account.id] = true;
    this.paymentMatchingService.toggleBankAccountCategorization(account.id, newEnabled).subscribe({
      next: () => {
        account.allowCategorization = newEnabled;
        this.toggleInProgress[account.id] = false;
      },
      error: () => {
        this.notificationService.error('Failed to update categorization');
        this.toggleInProgress[account.id] = false;
      },
    });
  }

  toggleAutoMatching(): void {
    const newValue = !this.autoMatchingEnabled;
    this.autoMatchingLoading = true;
    this.paymentMatchingService.updateAutoMatching(newValue).subscribe({
      next: settings => {
        this.autoMatchingEnabled = settings.autoMatchingEnabled;
        this.autoMatchingLoading = false;
        this.notificationService.success(settings.autoMatchingEnabled ? 'Auto-matching enabled' : 'Auto-matching disabled');
      },
      error: () => {
        this.notificationService.error('Failed to update auto-matching mode');
        this.autoMatchingLoading = false;
      },
    });
  }

  checkNow(): void {
    this.checkNowLoading = true;
    this.paymentMatchingService.runMatchingNow().subscribe({
      next: () => this.startPolling(),
      error: () => {
        this.notificationService.error('Failed to start payment matching');
        this.checkNowLoading = false;
      },
    });
  }

  private checkInitialStatus(): void {
    this.paymentMatchingService.getJobStatus().subscribe({
      next: status => {
        if (status.running) {
          this.checkNowLoading = true;
          this.startPolling();
        }
      },
    });
  }

  private startPolling(): void {
    this.stopPolling();
    this.statusPollInterval = setInterval(() => this.pollStatus(), 3000);
  }

  private stopPolling(): void {
    if (this.statusPollInterval) {
      clearInterval(this.statusPollInterval);
      this.statusPollInterval = null;
    }
  }

  private pollStatus(): void {
    this.paymentMatchingService.getJobStatus().subscribe({
      next: status => {
        if (!status.running) {
          this.stopPolling();
          this.checkNowLoading = false;
          if (status.error) {
            this.notificationService.error('Payment matching failed: ' + status.error);
          } else {
            this.notificationService.success('Payment matching completed');
          }
          this.loadBankAccounts();
          this.loadMatches();
          this.loadCategorizations();
        }
      },
      error: () => {
        this.stopPolling();
        this.checkNowLoading = false;
        this.notificationService.error('Failed to check matching status');
      },
    });
  }

  viewInvoice(invoiceId: string): void {
    this.invoice = null;
    this.detailsLoading = true;
    this.invoiceService.one(invoiceId).subscribe({
      next: invoice => (this.invoice = invoice),
      complete: () => (this.detailsLoading = false),
    });
    this.modal.alert().component(this.detailsModalTpl).open();
  }

  approveMatch(match: PaymentMatch): void {
    this.actionInProgress[match.id] = true;
    this.paymentMatchingService.approveMatch(match.id).subscribe({
      next: () => {
        this.notificationService.success('Match approved');
        this.loadMatches();
      },
      error: err => {
        const msg = paymentApprovalError(err, 'Failed to approve match');
        this.notificationService.error(msg);
        this.loadMatches();
      },
    });
  }

  rejectMatch(match: PaymentMatch): void {
    this.actionInProgress[match.id] = true;
    this.paymentMatchingService.rejectMatch(match.id).subscribe({
      next: () => {
        this.notificationService.success('Match rejected');
        this.loadMatches();
      },
      error: () => {
        this.notificationService.error('Failed to reject match');
        this.loadMatches();
      },
    });
  }

  approveCategorization(categorization: PaymentCategorization): void {
    this.categorizationActionInProgress[categorization.id] = true;
    this.paymentMatchingService.approveCategorization(categorization.id).subscribe({
      next: () => {
        this.notificationService.success('Categorization approved');
        this.loadCategorizations();
        this.loadMatches();
      },
      error: err => {
        const msg = paymentApprovalError(err, 'Failed to approve categorization');
        this.notificationService.error(msg);
        this.loadCategorizations();
      },
    });
  }

  rejectCategorization(categorization: PaymentCategorization): void {
    this.categorizationActionInProgress[categorization.id] = true;
    this.paymentMatchingService.rejectCategorization(categorization.id).subscribe({
      next: () => {
        this.notificationService.success('Categorization rejected');
        this.loadCategorizations();
      },
      error: () => {
        this.notificationService.error('Failed to reject categorization');
        this.loadCategorizations();
      },
    });
  }

  goToPage(page: number): void {
    if (page >= 0 && page < this.totalPages) {
      this.currentPage = page;
      this.loadMatches();
    }
  }

  onStatusFilterChange(status: string): void {
    this.selectedStatus = status ? (status as PaymentMatchStatus) : null;
    this.currentPage = 0;
    this.loadMatches();
  }

  goToCategorizationPage(page: number): void {
    if (page >= 0 && page < this.categorizationTotalPages) {
      this.categorizationCurrentPage = page;
      this.loadCategorizations();
    }
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'PENDING_REVIEW':
        return 'badge-warning';
      case 'APPROVED':
        return 'badge-info';
      case 'APPLIED':
        return 'badge-success';
      case 'REJECTED':
        return 'badge-secondary';
      case 'FAILED':
        return 'badge-danger';
      default:
        return 'badge-light';
    }
  }

  formatConfidence(confidence: number | null | undefined): string {
    if (confidence === null || confidence === undefined) {
      return '-';
    }
    return (confidence * 100).toFixed(0) + '%';
  }

  formatAmount(amountEur: number): string {
    return '€' + amountEur.toFixed(2);
  }

  formatTransactionDate(date: string): string {
    const [year, month, day] = date.split('-').map(Number);
    if (!year || !month || !day) {
      return date;
    }

    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(new Date(year, month - 1, day));
  }

  get pages(): number[] {
    const pages: number[] = [];
    for (let i = 0; i < this.totalPages; i++) {
      pages.push(i);
    }
    return pages;
  }

  get categorizationPages(): number[] {
    const pages: number[] = [];
    for (let i = 0; i < this.categorizationTotalPages; i++) {
      pages.push(i);
    }
    return pages;
  }
}
